/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/contexts/ToastContext.tsx
 * DESCRIPTION: Ephemeral toast notification system (success / error / info).
 *              Mounted above Routes so it works inside the plan gate,
 *              the onboarding wizard, and all pages.
 *
 * ARCHITECTURE:
 * - Toast popups provide immediate feedback (auto-dismiss after 5s)
 * - Also emits to NotificationContext for persistent record in Header
 * - Future notification center can consume the same events
 *
 * USAGE:
 *   const toast = useToast()
 *   toast.success('Subscription active!')
 *   toast.error('Voucher rejected')
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-09-24
 * ─────────────────────────────────────────────────────────────────────
 */
import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react'
import { useNotifications } from './NotificationContext'

type ToastType = 'success' | 'error' | 'info'

interface ToastItem {
  id: number
  type: ToastType
  message: string
}

interface ToastContextType {
  success: (message: string, title?: string) => void
  error: (message: string, title?: string) => void
  info: (message: string, title?: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const idRef = useRef(0)
  const { notify } = useNotifications()

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback(
    (type: ToastType, message: string, title?: string) => {
      // 1. Show ephemeral toast
      const id = ++idRef.current
      setToasts((prev) => [...prev, { id, type, message }])
      window.setTimeout(() => dismiss(id), 5000)

      // 2. Also emit to persistent notification center
      notify({
        type,
        title: title || (type === 'success' ? 'Success' : type === 'error' ? 'Error' : 'Info'),
        message,
        category: 'system',
      })
    },
    [dismiss, notify],
  )

  const value: ToastContextType = {
    success: (m, t) => push('success', m, t),
    error: (m, t) => push('error', m, t),
    info: (m, t) => push('info', m, t),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toaster overlay */}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-[calc(100vw-2rem)] sm:w-auto">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={[
              'flex items-start gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm',
              'bg-white dark:bg-gray-800',
              t.type === 'success'
                ? 'border-emerald-200 dark:border-emerald-800'
                : t.type === 'error'
                  ? 'border-red-200 dark:border-red-800'
                  : 'border-gray-200 dark:border-gray-700',
            ].join(' ')}
          >
            {t.type === 'success' && <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />}
            {t.type === 'error' && <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />}
            {t.type === 'info' && <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />}
            <p className="flex-1 text-gray-800 dark:text-gray-100">{t.message}</p>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}